/**
 * Classification types for offers based on their offer types
 */
export type OfferClassificationT = "private" | "business" | "mixed" | "unknown";

/**
 * Get the business classification of an offer based on its offer types
 *
 * @param offerTypes - Array of offer types associated with the offer
 * @returns Classification string for display purposes
 *
 * @example
 * ```tsx
 * const classification = getOfferTypeClassification(offer.offer_types);
 * // Returns: "Privatangebot", "Gewerbeangebot", "Gemischtes Angebot", "Unbekanntes Angebot"
 * ```
 */
export function getOfferTypeClassification(
  offerTypes?: Array<{ label: string; key?: string }> | null
): string {
  if (!offerTypes || offerTypes.length === 0) {
    return "";
  }

  const typeLabels = offerTypes.map((type) => type.label.toLowerCase());

  const hasPrivate = typeLabels.some((label) =>
    label.toLowerCase().includes("privat")
  );
  const hasBusiness = typeLabels.some((label) =>
    label.toLowerCase().includes("gewerbe")
  );

  // If both private and business types are present
  if (hasPrivate && hasBusiness) {
    return "Privat- und Gewerbeangebot";
  }

  // If only private types are present
  if (hasPrivate) {
    return "Privatangebot";
  }

  // If only business types are present
  if (hasBusiness) {
    return "Gewerbeangebot";
  }

  // If no recognizable classification found
  return "";
}

/**
 * Get the raw classification type without display formatting
 *
 * @param offerTypes - Array of offer types associated with the offer
 * @returns Raw classification type for programmatic use
 */
export function getOfferTypeClassificationType(
  offerTypes?: Array<{ label: string; key?: string }> | null
): OfferClassificationT {
  if (!offerTypes || offerTypes.length === 0) {
    return "unknown";
  }

  const typeLabels = offerTypes.map((type) => type.label.toLowerCase());

  const hasPrivate = typeLabels.some(
    (label) => label.includes("privat") || label.includes("private")
  );

  const hasBusiness = typeLabels.some(
    (label) =>
      label.includes("gewerbe") ||
      label.includes("business") ||
      label.includes("commercial") ||
      label.includes("gewerblich")
  );

  if (hasPrivate && hasBusiness) {
    return "mixed";
  }

  if (hasPrivate) {
    return "private";
  }

  if (hasBusiness) {
    return "business";
  }

  return "business"; // Default to business for unknown types
}
